
import './index.less';
import { useEffect, useRef, useState } from 'react';
import { ReactComponent as BigAddSvg } from '@/commons/icons/desktop-lib/add.svg';
import { ReactComponent as DeleteSvg } from '@/commons/icons/desktop-lib/delete.svg';
import { ReactComponent as EditSvg } from '@/commons/icons/desktop-lib/edit.svg';
import { Button, Form, Input, InputNumber, Modal, Select, Skeleton, Spin } from 'antd';
import classNames from 'classnames';
import { DEVICE_TYPE, DeviceTypeConfig } from '@/commons/enums/device-type';
import LibSensorPanel from './sensor';
import LibDataBoxPanel from './data-box';
import LibCablePanel from './cable';
import MonixModal from '@/commons/widget/monix-modal';
import { SENSOR_TYPE_LIST } from '@/commons/enums/sensor-type';
import { DATA_BOX_TYPE_LIST } from '@/commons/enums/data-box-type';
import { SIGNAL_TYPE_LIST } from '@/commons/enums/signal-type';
import { VERIFY_TYPE_LIST } from '@/commons/enums/verify-type';
import { GlobalMonitorParameter } from '@/commons/interface/maint/lib-device/global-monitor-parameter';
import { getGlobalMonitorParameters } from '@/api/maint/lib/lib-monitor-parameter';
import TnMessage from '@/commons/widget/message';
import TnModal from '@/commons/widget/base-modal';
import { getGlobalDevices } from '@/api/maint/lib/lib-device';

export interface Device {
    id?: number
    deviceType?: string
    type?: string
    marking?: string
    manufacturer?: string
}

export default function LibDevicePanel() {

    const [deviceList, setDeviceList] = useState<Device[]>([]) // 设备列表映射表
    const [loading, setLoading] = useState<boolean>(false) // loading
    const [activeItem, setActiveItem] = useState<Device>(null) // 激活的项

    const addBtnRef = useRef(null); // 添加表单的ref
    const editBtnRef = useRef(null); // 编辑表单的ref
    const [addForm] = Form.useForm(); // 添加表单
    const [editForm] = Form.useForm(); // 编辑表单

    const [showAddModel, setShowAddModel] = useState<boolean>(false) // 添加弹窗标志
    const [addLoading, setAddLoading] = useState<boolean>(false) // 添加弹窗loading
    const [showEditModel, setShowEditModel] = useState<boolean>(false) // 编辑弹窗标志
    const [editLoading, setEditLoading] = useState<boolean>(false) // 编辑弹窗loading
    const [currentDeviceType, setCurrentDeviceType] = useState<string>('') // 当前设备类型
    const [globalMonitorParameters, setGlobalMonitorParameters] = useState<GlobalMonitorParameter[]>([]) // 全局监测参数

    const [needRender, setNeedRender] = useState<boolean>(false) // 重新渲染

    // 初始化
    useEffect(() => {
        generateData(true);
    }, [])

    useEffect(() => {
        if (!showAddModel) {
            setCurrentDeviceType(null);
            addForm.resetFields();
        }
    }, [showAddModel])

    useEffect(() => {
        if (!showEditModel) {
            setCurrentDeviceType(null);
            editForm.resetFields();
        }
    }, [showEditModel])


    // 获取数据
    const generateData = (setActive = false) => {
        setLoading(true);
        getGlobalDevices().then(res => {
            if (res.status === 200) {
                let data: Device[] = res.data || [];
                setDeviceList(data);
                if (setActive && data?.length > 0) {
                    setActiveItem(data[0]);
                }
            }
        }).catch(err => {
            TnMessage.error("获取设备失败");
        }).finally(() => {
            setLoading(false);
        })
    }


    // 获取全局监测参数
    const generateGParameter = () => {
        getGlobalMonitorParameters().then(res => {
            if (res.status === 200) {
                let data: GlobalMonitorParameter[] = res.data || [];
                setGlobalMonitorParameters(data);
            }
        })
    }

    // 点击工具栏添加按钮
    const onAddClick = () => {
        setShowAddModel(true);
        generateGParameter();
    }

    // 点击工具栏编辑按钮
    const onEditClick = () => {
        if (!activeItem) {
            TnMessage.warning("未选中设备");
            return;
        }
        // 将信息注入
        const config = DeviceTypeConfig[activeItem?.deviceType];
        if (!config) {
            TnMessage.warning("找不到设备");
            return;
        }
        setShowEditModel(true);
        setCurrentDeviceType(activeItem?.deviceType);
        setEditLoading(true);
        generateGParameter();
        config.findDetailFunction(+activeItem?.id).then(res => {
            let data = res.data;
            editForm.setFieldsValue({ ...data });
        }).catch(err => {
            TnMessage.error("获取设备失败");
        }).finally(() => {
            setEditLoading(false);
        })
    }

    // 点击工具栏删除按钮
    const onDeleteClick = () => {
        if (!activeItem) {
            TnMessage.warning("未选中设备");
            return;
        }
        TnModal().deleteModal({
            onOk() {
                const config = DeviceTypeConfig[activeItem?.deviceType];
                if (config) {
                    setLoading(true);
                    Modal.destroyAll();
                    config.deleteFunction(activeItem?.id).then(res => {
                        if (res.status === 200) {
                            generateData(true);
                            TnMessage.success("删除成功");
                            setShowEditModel(false);
                        }
                    }).catch(err => {
                        TnMessage.error("删除失败");
                    }).finally(() => {
                        setLoading(false);
                    })
                }
            },
            onCancel() {
                Modal.destroyAll();
            },
            closable: true,
        })
    }

    // 点击添加弹窗确认按钮
    const onAddSaveClick = () => {
        if (addLoading) return;
        if (addBtnRef.current) {
            addBtnRef.current.click();
        }
    }

    // 创建表单提交
    const onAddFinish = (values) => {
        let { deviceType } = values;
        const config = DeviceTypeConfig[deviceType];

        if (config) {
            setAddLoading(true);
            let data = { ...values };
            if (deviceType === DEVICE_TYPE.SENSOR) {
                data = {
                    ...data,
                    type: values.sensorType
                }
            }
            config.createFunction(data).then(res => {
                if (res.status === 200) {
                    generateData(false);
                    let newItem: Device = {
                        id: res.data,
                        deviceType: deviceType,
                    }
                    setActiveItem(newItem);
                    TnMessage.success("添加成功");
                    setShowAddModel(false);
                }
            }).catch(err => {
                TnMessage.error(err.data || '添加失败');
            }).finally(() => {
                setAddLoading(false);
            });
        }
    }

    // 点击编辑弹窗确认按钮
    const onEditSaveClick = () => {
        if (editLoading) return;
        if (editBtnRef.current) {
            editBtnRef.current.click();
        }
    }

    // 编辑表单提交
    const onEditFinish = (values) => {
        const config = DeviceTypeConfig[activeItem?.deviceType];

        if (config) {
            setEditLoading(true);
            let data = { ...values };
            if (activeItem?.type === DEVICE_TYPE.SENSOR) {
                data = {
                    ...data,
                    type: values.sensorType
                }
            }
            config.updateFunction(activeItem?.id, data).then(res => {
                if (res.status === 200) {
                    generateData(false);
                    setNeedRender(pre => !pre);
                    TnMessage.success("编辑成功");
                    setShowEditModel(false);
                }
            }).catch(err => {
                TnMessage.error(err?.data || "编辑失败");
            }).finally(() => {
                setEditLoading(false);
            });
        }
    }

    return (
        <div className='lib-device-container'>
            <div className="ope-header">
                <div className="create-device-btn" onClick={onAddClick}><BigAddSvg />添加设备</div>
                {/* <div className='ope-icon'><SelectSvg /></div> */}
                {/* <div className='ope-icon' onClick={onAddClick}><AddSvg /></div> */}
                <div className='ope-icon' onClick={onEditClick}><EditSvg /></div>
                <div className='ope-icon' onClick={onDeleteClick}><DeleteSvg /></div>
            </div>
            <div className="lib-device-panel">
                <Skeleton loading={loading}>
                    <div className="lib-device-list">
                        <div className="lib-header">
                            <div className='header-cell-1'>设备类别</div>
                            <div className='header-cell-2'>设备厂商</div>
                            <div className='header-cell-3'>设备型号</div>
                        </div>
                        <div className="lib-body">
                            {deviceList?.map((item: Device, key) => {
                                let showDivider = false;
                                let type = item?.type || '';
                                let manufacturer = item?.manufacturer || '';
                                let marking = item?.marking || '';
                                if (key > 0) {
                                    let typeEqualFlag = deviceList[key]?.type === deviceList[key - 1]?.type;
                                    if (typeEqualFlag) {
                                        type = '';
                                    }
                                    if (typeEqualFlag && deviceList[key]?.manufacturer === deviceList[key - 1]?.manufacturer) {
                                        manufacturer = '';
                                    }
                                    if (deviceList[key]?.type !== deviceList[key - 1]?.type) {
                                        showDivider = true;
                                    }
                                }
                                return (
                                    <>
                                        {showDivider && <div className='divider'></div>}
                                        <div className={classNames({
                                            "body-row": true,
                                            "body-row-active": activeItem?.id === item?.id && activeItem?.deviceType === item?.deviceType,
                                        })} key={key} onClick={() => setActiveItem(item)}>
                                            <div className='body-cell-1'>{type}</div>
                                            <div className='body-cell-2'>{manufacturer}</div>
                                            <div className='body-cell-3'>{marking}</div>
                                        </div>
                                    </>
                                )
                            })}
                        </div>
                    </div>
                </Skeleton>
                <div className="lib-device-detail">
                    <div className='lib-header'>设备详情</div>
                    {activeItem?.deviceType === DEVICE_TYPE.SENSOR && <LibSensorPanel sensorId={activeItem?.id} needRender={needRender} />}
                    {activeItem?.deviceType === DEVICE_TYPE.DATA_BOX && <LibDataBoxPanel dataBoxId={activeItem?.id} needRender={needRender} />}
                    {activeItem?.deviceType === DEVICE_TYPE.CABLE && <LibCablePanel cableId={activeItem?.id} needRender={needRender} />}
                </div>
            </div >

            <MonixModal
                title={"创建设备"}
                visible={showAddModel}
                setVisible={(e) => setShowAddModel(e)}
                onSaveClick={onAddSaveClick}
                wrapClassName="form-modal"
                saveDisable={addLoading}
            >
                <Spin wrapperClassName="form-body" spinning={addLoading}>
                    <Form
                        colon={false}
                        form={addForm}
                        autoComplete="off"
                        labelCol={{ span: 6 }}
                        onFinish={onAddFinish}
                        initialValues={{
                            autoPush: false,
                            sensorStandalone: false,
                        }}
                    >
                        <Form.Item label="设备类型:" name="deviceType" rules={[{ required: true, message: '设备类型不能为空' }]}>
                            <Select onChange={e => setCurrentDeviceType(e)}>
                                {/* <Select.Option value={DEVICE_TYPE.SERVER}>服务器</Select.Option> */}
                                <Select.Option value={DEVICE_TYPE.DATA_BOX}>采集箱</Select.Option>
                                <Select.Option value={DEVICE_TYPE.SENSOR}>传感器</Select.Option>
                                <Select.Option value={DEVICE_TYPE.CABLE}>数据线缆</Select.Option>
                            </Select>
                        </Form.Item>
                        {currentDeviceType === DEVICE_TYPE.SENSOR && <>
                            <Form.Item label="传感器类型:" name="sensorType" rules={[{ required: true, message: '传感器类型不能为空' }]}>
                                <Select>
                                    {SENSOR_TYPE_LIST.map(item => (
                                        <Select.Option key={item.value} value={item.value}>{item.label}</Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </>}
                        {currentDeviceType === DEVICE_TYPE.DATA_BOX && <>
                            <Form.Item label="采集设备类型:" name="type" rules={[{ required: true, message: '采集设备类型不能为空' }]}>
                                <Select>
                                    {DATA_BOX_TYPE_LIST.map(item => (
                                        <Select.Option key={item.value} value={item.value}>{item.label}</Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </>}
                        <Form.Item label="名称:" name="name" rules={[{ required: true, message: '名称不能为空' }]}>
                            <Input />
                        </Form.Item>
                        <Form.Item label="厂商:" name="manufacturer" rules={[{ required: true, message: '厂商不能为空' }]}>
                            <Input />
                        </Form.Item>
                        <Form.Item label="型号:" name="marking" rules={[{ required: true, message: '型号不能为空' }]}>
                            <Input />
                        </Form.Item>
                        {currentDeviceType === DEVICE_TYPE.DATA_BOX && <>
                            <Form.Item label="通道数量:" name="channelCount" rules={[{ required: true, message: '通道数量不能为空' }]}>
                                <InputNumber style={{ width: '100%' }} min={0} />
                            </Form.Item>
                            <Form.Item label="信号类型:" name="signalType" rules={[{ required: true, message: '信号类型不能为空' }]}>
                                <Select>
                                    {SIGNAL_TYPE_LIST.map(item => (
                                        <Select.Option key={item.value} value={item.value}>{item.label}</Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                            <Form.Item label="自动推送数据:" name="autoPush" rules={[{ required: true, message: '自动推送数据不能为空' }]}>
                                <Select defaultValue={false}>
                                    <Select.Option value={true}>是</Select.Option>
                                    <Select.Option value={false}>否</Select.Option>
                                </Select>
                            </Form.Item>
                            <Form.Item label="独立传感器:" name="sensorStandalone" rules={[{ required: true, message: '独立传感器不能为空' }]}>
                                <Select defaultValue={false}>
                                    <Select.Option value={true}>是</Select.Option>
                                    <Select.Option value={false}>否</Select.Option>
                                </Select>
                            </Form.Item>
                        </>}
                        {currentDeviceType === DEVICE_TYPE.SENSOR && <>
                            <Form.Item label="精度:" name="precision" rules={[{ required: true, message: '精度不能为空' }]}>
                                <InputNumber style={{ width: '100%' }} min={0} precision={0} />
                            </Form.Item>
                            <Form.Item label="量程:" name="range" rules={[{ required: true, message: '量程不能为空' }]}>
                                <Input />
                            </Form.Item>
                            <Form.Item label="信号类型:" name="signalType" rules={[{ required: true, message: '信号类型不能为空' }]}>
                                <Select>
                                    {SIGNAL_TYPE_LIST.map(item => (
                                        <Select.Option key={item.value} value={item.value}>{item.label}</Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                            <Form.Item label="验证类型:" name="verifyType">
                                <Select>
                                    {VERIFY_TYPE_LIST.map(item => (
                                        <Select.Option key={item.value} value={item.value}>{item.label}</Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                            <Form.Item label="英文名称:" name="englishName">
                                <Input />
                            </Form.Item>
                            <Form.Item label="计算公式:" name="formulaStr">
                                <Input />
                            </Form.Item>
                            <Form.Item label="监测参数:" name="globalParameterIds">
                                <Select mode="tags">
                                    {globalMonitorParameters?.map(item => (
                                        <Select.Option key={item.id} value={item.id}>{item?.name || ''}</Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </>}
                        {currentDeviceType === DEVICE_TYPE.CABLE && <>
                            <Form.Item label="规格:" name="specification" rules={[{ required: true, message: '规格不能为空' }]}>
                                <Input />
                            </Form.Item>
                        </>}
                        <Button ref={addBtnRef} htmlType="submit" style={{ display: 'none' }}></Button>
                    </Form>
                </Spin>
            </MonixModal>

            <MonixModal
                title={"编辑设备"}
                visible={showEditModel}
                setVisible={(e) => setShowEditModel(e)}
                onSaveClick={onEditSaveClick}
                wrapClassName="form-modal"
                saveDisable={editLoading}
            >
                <Spin wrapperClassName="form-body" spinning={editLoading}>
                    <Form
                        colon={false}
                        form={editForm}
                        autoComplete="off"
                        labelCol={{ span: 6 }}
                        onFinish={onEditFinish}
                    >
                        <Form.Item label="名称:" name="name" rules={[{ required: true, message: '名称不能为空' }]}>
                            <Input />
                        </Form.Item>
                        <Form.Item label="厂商:" name="manufacturer" rules={[{ required: true, message: '厂商不能为空' }]}>
                            <Input />
                        </Form.Item>
                        <Form.Item label="型号:" name="marking" rules={[{ required: true, message: '型号不能为空' }]}>
                            <Input />
                        </Form.Item>
                        {currentDeviceType === DEVICE_TYPE.DATA_BOX && <>
                            <Form.Item label="通道数量:" name="channelCount" rules={[{ required: true, message: '通道数量不能为空' }]}>
                                <InputNumber style={{ width: '100%' }} min={0} />
                            </Form.Item>
                            <Form.Item label="信号类型:" name="signalType" rules={[{ required: true, message: '信号类型不能为空' }]}>
                                <Select>
                                    {SIGNAL_TYPE_LIST.map(item => (
                                        <Select.Option key={item.value} value={item.value}>{item.label}</Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                            <Form.Item label="自动推送数据:" name="autoPush" rules={[{ required: true, message: '自动推送数据不能为空' }]}>
                                <Select>
                                    <Select.Option value={true}>是</Select.Option>
                                    <Select.Option value={false}>否</Select.Option>
                                </Select>
                            </Form.Item>
                            <Form.Item label="独立传感器:" name="sensorStandalone" rules={[{ required: true, message: '独立传感器不能为空' }]}>
                                <Select>
                                    <Select.Option value={true}>是</Select.Option>
                                    <Select.Option value={false}>否</Select.Option>
                                </Select>
                            </Form.Item>
                        </>}
                        {currentDeviceType === DEVICE_TYPE.SENSOR && <>
                            <Form.Item label="精度:" name="precision" rules={[{ required: true, message: '精度不能为空' }]}>
                                <InputNumber style={{ width: '100%' }} min={0} precision={0} />
                            </Form.Item>
                            <Form.Item label="量程:" name="range" rules={[{ required: true, message: '量程不能为空' }]}>
                                <Input />
                            </Form.Item>
                            <Form.Item label="信号类型:" name="signalType" rules={[{ required: true, message: '信号类型不能为空' }]}>
                                <Select>
                                    {SIGNAL_TYPE_LIST.map(item => (
                                        <Select.Option key={item.value} value={item.value}>{item.label}</Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                            <Form.Item label="验证类型:" name="verifyType" rules={[{ required: true, message: '验证类型不能为空' }]}>
                                <Select>
                                    {VERIFY_TYPE_LIST.map(item => (
                                        <Select.Option key={item.value} value={item.value}>{item.label}</Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                            <Form.Item label="英文名称:" name="englishName">
                                <Input />
                            </Form.Item>
                            <Form.Item label="计算公式:" name="formulaStr">
                                <Input />
                            </Form.Item>
                            <Form.Item label="监测参数:" name="globalParameterIds">
                                <Select mode="tags">
                                    {globalMonitorParameters?.map(item => (
                                        <Select.Option key={item.id} value={item.id}>{item?.name || ''}</Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </>}
                        {currentDeviceType === DEVICE_TYPE.CABLE && <>
                            <Form.Item label="规格:" name="specification" rules={[{ required: true, message: '规格不能为空' }]}>
                                <Input />
                            </Form.Item>
                        </>}
                        <Button ref={editBtnRef} htmlType="submit" style={{ display: 'none' }}></Button>
                    </Form>
                </Spin>
            </MonixModal>
        </div >
    )
}